'use client';

import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import type { FormActionPayload, FormActionType } from '../../Shibuya';
import { useFormAction } from '../FormAction';

type FireFieldInteractedExtra = {
  field?: string;
  fieldType?: string;
  fieldValue?: string;
  additionalAttributes?: FormActionPayload['additional_attributes'];
};

export type FormActionViewedContextValue = {
  fireFieldInteracted: (extra: FireFieldInteractedExtra) => void;
  fireFormAction: (action: FormActionType, extra?: FireFieldInteractedExtra) => void;
};

export const FormActionViewedContext = createContext<FormActionViewedContextValue | null>(null);

export type FormActionViewedProps = {
  formName: string;
  children: React.ReactNode;
  /**
   * When provided, fires `viewed` imperatively when `isOpen` becomes true (dialog/drawer pattern).
   * When omitted, fires `viewed` via viewport intersection (scroll-into-view pattern).
   */
  isOpen?: boolean;
  /** Included as `section_name` on every Form Action event for this form. */
  sectionName?: string;
};

export function FormActionViewed({
  formName,
  children,
  isOpen,
  sectionName,
}: FormActionViewedProps) {
  const { fireFormAction } = useFormAction(formName, { sectionName });
  const imperativeMode = isOpen !== undefined;
  const { ref: inViewRef, inView } = useInView({ triggerOnce: true, skip: imperativeMode });
  // Latches after a confirmed send; resets when isOpen goes false so real reopens start a new funnel.
  const viewedFiredRef = useRef(false);

  useEffect(() => {
    // Reset on modal close so reopening starts a fresh funnel.
    if (isOpen === false) {
      viewedFiredRef.current = false;
      return;
    }
    const shouldFire = imperativeMode ? isOpen : inView;
    if (!shouldFire || viewedFiredRef.current) return;
    // Latch only when the call actually tracked (pageViewReady && deviceId were ready).
    if (fireFormAction('viewed')) {
      viewedFiredRef.current = true;
    }
  }, [inView, isOpen, fireFormAction, imperativeMode]);

  const fireFieldInteracted = useCallback(
    (extra: FireFieldInteractedExtra) => {
      fireFormAction('field-interacted', extra);
    },
    [fireFormAction],
  );

  return (
    <FormActionViewedContext.Provider value={{ fireFieldInteracted, fireFormAction }}>
      {imperativeMode ? children : <div ref={inViewRef}>{children}</div>}
    </FormActionViewedContext.Provider>
  );
}

export function useFormActionViewedContext() {
  const context = useContext(FormActionViewedContext);
  if (!context) {
    throw new Error('useFormActionViewedContext must be used within FormActionViewed');
  }
  return context;
}
